import React, { useState, useEffect, useRef } from 'react';
import { dynastyTokens } from '../../../services/colorService';

const colors = dynastyTokens.colors;

const DEFAULT_PHASES = [
  { label: 'Analyzing your roster...', duration: 1000, progress: 10 },
  { label: 'Evaluating player values...', duration: 1500, progress: 20 },
  { label: 'Scanning league needs...', duration: 1000, progress: 30 },
  { label: 'Modeling trade motivations...', duration: 800, progress: 38 },
  { label: 'Simulating draft scenarios...', duration: 3000, progress: 55 },
  { label: 'Generating trade packages...', duration: 2000, progress: 65 },
  { label: 'Validating constraints...', duration: 500, progress: 70 },
  { label: 'Running trade simulations...', duration: 8000, progress: 90 },
  { label: 'Ranking recommendations...', duration: 1500, progress: 98 },
];

const OptimizerLoadingBar = ({ isLoading }) => {
  const [currentPhase, setCurrentPhase] = useState(0);
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(true);
  const timersRef = useRef([]);

  useEffect(() => {
    if (!isLoading) {
      // Response arrived — snap to 100% and fade out
      setProgress(100);
      setCurrentPhase(DEFAULT_PHASES.length - 1);
      const hideTimer = setTimeout(() => setVisible(false), 400);
      return () => clearTimeout(hideTimer);
    }

    // Starting a new load — reset everything
    setVisible(true);
    setCurrentPhase(0);
    setProgress(0);

    // Clear any existing timers
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    // Advance through phases on a timer
    let elapsed = 0;
    DEFAULT_PHASES.forEach((phase, i) => {
      elapsed += i === 0 ? 200 : DEFAULT_PHASES[i - 1].duration;
      const timer = setTimeout(() => {
        setCurrentPhase(i);
        setProgress(phase.progress);
      }, elapsed);
      timersRef.current.push(timer);
    });

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [isLoading]);

  if (!visible) return null;

  const phase = DEFAULT_PHASES[currentPhase] || DEFAULT_PHASES[DEFAULT_PHASES.length - 1];

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '80px 20px',
      gap: '16px',
      minHeight: '400px',
    }}>
      {/* Phase label */}
      <div style={{
        fontSize: '14px',
        color: colors.neutral[400],
        fontWeight: 500,
        transition: 'opacity 0.3s ease',
      }}>
        {phase.label}
      </div>

      {/* Progress bar track */}
      <div style={{
        width: '100%',
        maxWidth: '400px',
        height: '6px',
        backgroundColor: colors.neutral[800],
        borderRadius: '3px',
        overflow: 'hidden',
      }}>
        {/* Progress bar fill */}
        <div style={{
          height: '100%',
          width: `${progress}%`,
          backgroundColor: colors.primary,
          borderRadius: '3px',
          transition: progress === 100 ? 'width 0.3s ease-out' : 'width 0.8s ease-out',
        }} />
      </div>

      {/* Percentage */}
      <div style={{
        fontSize: '12px',
        color: colors.neutral[500],
      }}>
        {Math.round(progress)}%
      </div>
    </div>
  );
};

export default OptimizerLoadingBar;
